/**
 * Persistent image cache backed by Cache Storage. Survives reloads and app
 * restarts, unlike the in-memory maps layered above it.
 */

import { canonicalImageKey } from "src/utils/imageUrl.js";
import {
  imageChecks,
  imagePalettes,
  processedKeys,
} from "src/utils/imageDecisionStore.js";
import { prefetchAllowed, whenIdle } from "src/utils/schedulePrefetch.js";

/** Bump to discard every persisted image after a deploy. */
export const IMAGE_CACHE_VERSION = 2;
/** Bump when `removeImageBackground` changes, to discard only the derivatives. */
export const PROCESSING_VERSION = 1;

const CACHE_PREFIX = "stanza-images";
/** Must match `PROCESSED_PREFIX` in public/sw.js. */
const PROCESSED_PATH_PREFIX = "/__img-cache__/processed/";
const ORIGINALS_CACHE = `${CACHE_PREFIX}-v${IMAGE_CACHE_VERSION}`;
const PROCESSED_CACHE = `${CACHE_PREFIX}-processed-v${IMAGE_CACHE_VERSION}-${PROCESSING_VERSION}`;

const CACHED_AT_HEADER = "x-stanza-cached-at";
const BYTES_HEADER = "x-stanza-bytes";
const HASH_HEADER = "x-stanza-hash";
/** Hash of the original a derivative was built from. */
const SOURCE_HASH_HEADER = "x-stanza-source-hash";

/** How long an entry is served before a background revalidation is queued. */
const SOFT_TTL_MS = 60 * 60 * 1000;
/** How long an entry survives at all, regardless of use. */
const HARD_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** Byte budget per cache, enforced by the sweep. */
const CACHE_BYTE_BUDGET = 20 * 1024 * 1024;
/** Share of the origin quota this feature may occupy across both caches. */
const QUOTA_SHARE = 0.25;
const SWEEP_INTERVAL_MS = 12 * 60 * 60 * 1000;
const SWEEP_STAMP_KEY = "stanza:img-cache-sweep";

let fallbackMode = false;
/** Checks queued or running, so one image is never checked twice at a time. */
const queuedChecks = new Set();
/** Images whose bytes changed while this page was open; their renderings are stale. */
const changedSources = new Set();
/** Hash of the original bytes last read or written, per key. */
const originalHashes = new Map();

/**
 * Emits a structured log line for GCP Logging.
 * @param {string} severity
 * @param {string} event
 * @param {object} [details]
 */
const logImageCacheEvent = (severity, event, details = {}) => {
  console.log(
    JSON.stringify({
      severity,
      source: "imageCacheStorage",
      event,
      ...details,
    })
  );
};

const getErrorMessage = (error) =>
  error instanceof Error ? error.message : String(error);

/**
 * Degrades to the in-memory caches for the rest of the session. Cache Storage is
 * absent in insecure contexts and in some private-browsing modes.
 * @param {string} reason
 */
const degrade = (reason) => {
  if (fallbackMode) return;
  fallbackMode = true;
  logImageCacheEvent("WARNING", "fallback_mode_enabled", { reason });
};

/** @returns {CacheStorage|null} */
const cacheStorage = () => (typeof caches !== "undefined" ? caches : null);

/**
 * Opens a named cache, degrading instead of throwing when unavailable.
 * @param {string} name
 * @returns {Promise<Cache|null>}
 */
const openCache = async (name) => {
  if (fallbackMode) return null;

  const storage = cacheStorage();
  if (!storage) {
    degrade("Cache Storage is unavailable in this context.");
    return null;
  }

  try {
    return await storage.open(name);
  } catch (error) {
    degrade(getErrorMessage(error));
    return null;
  }
};

/**
 * Cache Storage keys must be http(s) URLs, so derivatives get a synthetic
 * same-origin key. The service worker answers this path from Cache Storage, so
 * the same string doubles as a paintable `src`.
 * @param {string} key
 * @returns {string}
 */
const processedRequestUrl = (key) =>
  `${window.location.origin}${PROCESSED_PATH_PREFIX}${encodeURIComponent(key)}`;

/**
 * Whether a worker is currently controlling this page. Without one the
 * synthetic path has nothing serving it, so callers must not paint it.
 * @returns {boolean}
 */
const serviceWorkerControls = () =>
  typeof navigator !== "undefined" && !!navigator.serviceWorker?.controller;

/**
 * A paintable URL for an image's stored derivative, resolved synchronously.
 * Returns null when there is no derivative or nothing to serve it, in which case
 * the caller falls back to reading the bytes itself.
 * @param {string} url
 * @returns {string|null}
 */
export const processedImageUrl = (url) => {
  const key = canonicalImageKey(url);
  if (!key || !serviceWorkerControls() || !processedKeys.has(key)) return null;
  return processedRequestUrl(key);
};

/**
 * Records that a derivative exists, for a caller that found one the synchronous
 * index did not know about.
 * @param {string} url
 */
export const rememberProcessed = (url) => {
  const key = canonicalImageKey(url);
  if (key) processedKeys.set(key, 1);
};

/** @returns {boolean} Whether entries can be hashed, and so verified, here. */
const hashingAvailable = () => !!window.crypto?.subtle;

/**
 * @param {ArrayBuffer} buffer
 * @returns {Promise<string>} Hex digest, or an empty string without WebCrypto.
 */
const sha256Hex = async (buffer) => {
  if (!window.crypto?.subtle) return "";
  try {
    const digest = await window.crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
};

/**
 * Wraps bytes in a Response carrying our own metadata. Reconstructing the
 * Response is what makes the metadata readable: a CORS response from the CDN
 * hides its ETag unless the bucket exposes it.
 * @param {ArrayBuffer} buffer
 * @param {string} contentType
 * @param {string} hash
 * @param {string} [sourceHash] - Set on derivatives only.
 * @returns {Response}
 */
const buildEntry = (buffer, contentType, hash, sourceHash) =>
  new Response(buffer, {
    headers: {
      "content-type": contentType || "image/png",
      [CACHED_AT_HEADER]: String(Date.now()),
      [BYTES_HEADER]: String(buffer.byteLength),
      [HASH_HEADER]: hash,
      ...(sourceHash === undefined ? {} : { [SOURCE_HASH_HEADER]: sourceHash }),
    },
  });

/**
 * @param {string} uri - A base64 data URI, the form sources are read as.
 * @returns {Promise<string>} Hex digest of its bytes, or an empty string.
 */
const hashDataUri = async (uri) => {
  if (!uri.startsWith("data:")) return "";
  try {
    // Decoded in place rather than fetched: a page CSP may block data: URIs.
    const bytes = Uint8Array.from(atob(uri.slice(uri.indexOf(",") + 1)), (char) =>
      char.charCodeAt(0)
    );
    return await sha256Hex(bytes.buffer);
  } catch {
    return "";
  }
};

/**
 * @param {Response} response
 * @returns {{ cachedAt: number, bytes: number, hash: string, stale: boolean }}
 */
const readMetadata = (response) => {
  const cachedAt = Number(response.headers.get(CACHED_AT_HEADER)) || 0;
  return {
    cachedAt,
    bytes: Number(response.headers.get(BYTES_HEADER)) || 0,
    hash: response.headers.get(HASH_HEADER) || "",
    stale: Date.now() - cachedAt > SOFT_TTL_MS,
  };
};

/**
 * @param {string} name
 * @param {string} requestUrl
 * @returns {Promise<{ blob: Blob, cachedAt: number, hash: string, stale: boolean }|null>}
 */
const readEntry = async (name, requestUrl) => {
  const cache = await openCache(name);
  if (!cache) return null;

  try {
    const response = await cache.match(requestUrl);
    if (!response) return null;

    const metadata = readMetadata(response);
    const blob = await response.blob();
    return { blob, ...metadata };
  } catch (error) {
    logImageCacheEvent("ERROR", "read_failed", {
      error: getErrorMessage(error),
    });
    return null;
  }
};

/**
 * @param {string} name
 * @param {string} requestUrl
 * @param {Blob} blob
 * @param {string} [sourceHash]
 * @returns {Promise<string|undefined>} The stored bytes' hash.
 */
const writeEntry = async (name, requestUrl, blob, sourceHash) => {
  const cache = await openCache(name);
  if (!cache || !blob) return;

  try {
    const buffer = await blob.arrayBuffer();
    const hash = await sha256Hex(buffer);
    await cache.put(requestUrl, buildEntry(buffer, blob.type, hash, sourceHash));
    return hash;
  } catch (error) {
    // A quota overflow is expected under storage pressure; the sweep reclaims it.
    logImageCacheEvent("WARNING", "write_failed", {
      error: getErrorMessage(error),
    });
  }
};

/**
 * Forgets a derivative everywhere it is recorded, so the next load rebuilds it.
 * @param {string} key
 * @returns {Promise<void>}
 */
const dropDerivative = async (key) => {
  const processed = await openCache(PROCESSED_CACHE);
  await processed?.delete(processedRequestUrl(key));
  processedKeys.delete(key);
  imagePalettes.delete(key);
  // Whatever is rebuilt next has to earn its own check rather than inherit this one.
  imageChecks.delete(key);
};

/**
 * @param {string} key
 * @returns {Promise<string|null>} The stored original's hash, or null without one.
 */
const storedOriginalHash = async (key) => {
  const originals = await openCache(ORIGINALS_CACHE);
  const original = await originals?.match(key);
  if (!original) return null;

  const { hash } = readMetadata(original);
  originalHashes.set(key, hash);
  return hash;
};

/**
 * @param {string} key
 * @returns {Promise<string|null>} Hash of the source a derivative was built from.
 */
const storedSourceStamp = async (key) => {
  const processed = await openCache(PROCESSED_CACHE);
  const derivative = await processed?.match(processedRequestUrl(key));
  return derivative ? derivative.headers.get(SOURCE_HASH_HEADER) : null;
};

/**
 * Validates a derivative against the source it was stamped with: one built from
 * other bytes is dropped, a stale original is refreshed.
 * @param {string} url
 * @param {string} key
 * @returns {Promise<void>}
 */
const verifyDerivative = async (url, key) => {
  if (!hashingAvailable()) return;

  const processed = await openCache(PROCESSED_CACHE);
  const derivative = await processed?.match(processedRequestUrl(key));
  if (!derivative) return;

  // Stored before stamping existed, so it cannot be verified: drop it once and
  // let the next load rebuild a stamped one. Nothing says the bytes changed.
  const stamp = derivative.headers.get(SOURCE_HASH_HEADER);
  if (!stamp) {
    await dropDerivative(key);
    logImageCacheEvent("INFO", "derivative_invalidated", { key, reason: "unstamped" });
    return;
  }

  const originals = await openCache(ORIGINALS_CACHE);
  const original = await originals?.match(key);
  // The sweep evicts each cache on its own budget, so an original can be gone
  // while its derivative remains; the source itself settles it.
  if (!original) return revalidateEntry(url);

  const { hash, stale } = readMetadata(original);
  originalHashes.set(key, hash);
  if (stamp !== hash) {
    changedSources.add(key);
    await dropDerivative(key);
    logImageCacheEvent("INFO", "derivative_invalidated", { key, reason: "source_mismatch" });
    return;
  }

  if (stale) await revalidateEntry(url);
  else imageChecks.set(key, Date.now());
};

/**
 * Reads a downloaded original.
 * @param {string} url
 * @returns {Promise<{ blob: Blob, cachedAt: number, hash: string, stale: boolean }|null>}
 */
export const getOriginal = async (url) => {
  const key = canonicalImageKey(url);
  if (!key) return null;

  const entry = await readEntry(ORIGINALS_CACHE, key);
  if (entry?.hash) originalHashes.set(key, entry.hash);
  return entry;
};

/**
 * @param {string} url
 * @param {Blob} blob
 * @returns {Promise<void>}
 */
export const putOriginal = async (url, blob) => {
  const key = canonicalImageKey(url);
  if (!key) return;

  const hash = await writeEntry(ORIGINALS_CACHE, key, blob);
  if (hash) originalHashes.set(key, hash);
};

/**
 * Reads a background-removed derivative.
 * @param {string} url
 * @returns {Promise<{ blob: Blob, cachedAt: number, hash: string, stale: boolean }|null>}
 */
export const getProcessed = (url) => {
  const key = canonicalImageKey(url);
  return key
    ? readEntry(PROCESSED_CACHE, processedRequestUrl(key))
    : Promise.resolve(null);
};

/**
 * Hash of the bytes a derivative is about to be built from: the stored
 * original's when known, otherwise the source decoded in place.
 * @param {string} url
 * @param {string} sourceUri - Base64 data URI of the bytes in hand.
 * @returns {Promise<string>}
 */
export const sourceHashFor = async (url, sourceUri) => {
  const key = canonicalImageKey(url);
  if (!key) return "";
  return (
    originalHashes.get(key) ??
    (await storedOriginalHash(key)) ??
    (await hashDataUri(sourceUri))
  );
};

/**
 * Stores a derivative stamped with the hash of the source it was built from.
 * Skips the write when the stored original has since changed.
 * @param {string} url
 * @param {Blob} blob
 * @param {{ sourceHash?: string }} [options] - From `sourceHashFor`, read before
 *   the build so a source replaced meanwhile is detected here.
 * @returns {Promise<void>}
 */
export const putProcessed = async (url, blob, { sourceHash } = {}) => {
  const key = canonicalImageKey(url);
  if (!key) return;

  const originalHash = originalHashes.get(key) ?? (await storedOriginalHash(key));
  const staleBuild =
    changedSources.has(key) || (sourceHash && originalHash && sourceHash !== originalHash);
  if (staleBuild) {
    logImageCacheEvent("INFO", "derivative_write_skipped", { key });
    return;
  }

  await writeEntry(PROCESSED_CACHE, processedRequestUrl(key), blob, sourceHash);
  // Recorded only after the write lands, so the synchronous index never claims
  // a derivative the worker would fail to find.
  processedKeys.set(key, 1);
};

/**
 * Opens the caches and loads their indexes at boot. The first access to a cache
 * store costs an order of magnitude more than later ones, and paying it here
 * keeps that cost off the first tile that needs art.
 *
 * Deliberately does not read entry bodies: `URL.createObjectURL` is a
 * synchronous trip to the blob registry, so preloading a screenful would block
 * the main thread for longer than the loading state it was meant to avoid.
 *
 * @returns {Promise<void>}
 */
export const warmCaches = async () => {
  try {
    const caches = await Promise.all([
      openCache(ORIGINALS_CACHE),
      openCache(PROCESSED_CACHE),
    ]);
    await Promise.all(caches.map((cache) => cache?.keys()));
  } catch (error) {
    logImageCacheEvent("WARNING", "warm_failed", {
      error: getErrorMessage(error),
    });
  }
};

/**
 * Drops an image and its derivative. Call when the asset behind a stable URL is
 * known to have changed, such as right after an upload.
 * @param {string} url
 * @returns {Promise<void>}
 */
export const evict = async (url) => {
  const key = canonicalImageKey(url);
  if (!key) return;
  imagePalettes.delete(key);
  imageChecks.delete(key);
  originalHashes.delete(key);
  // An upload is the strongest signal the bytes changed, so anything sampled
  // from the outgoing copies is refused persistence for the rest of the load.
  changedSources.add(key);

  try {
    const originals = await openCache(ORIGINALS_CACHE);
    await originals?.delete(key);

    const processed = await openCache(PROCESSED_CACHE);
    await processed?.delete(processedRequestUrl(key));
    processedKeys.delete(key);
  } catch (error) {
    // Callers fire this off after an upload and do not await it.
    logImageCacheEvent("WARNING", "evict_failed", {
      error: getErrorMessage(error),
    });
  }
};

/**
 * Refreshes a cached entry now. The browser attaches its own validators, so an
 * unchanged image costs a 304 and no body transfer.
 * @param {string} url
 * @returns {Promise<void>}
 */
export const revalidateEntry = async (url) => {
  const key = canonicalImageKey(url);
  const cache = await openCache(ORIGINALS_CACHE);
  if (!cache) return;

  const existing = await cache.match(key);
  // Without a stored original, the derivative's own stamp is what the fresh
  // bytes are compared against.
  const previousHash = existing
    ? readMetadata(existing).hash
    : await storedSourceStamp(key);
  if (!existing && !previousHash) return;

  const response = await fetch(url, { cache: "no-cache" });
  if (!response.ok) return;

  const buffer = await response.arrayBuffer();
  const hash = await sha256Hex(buffer);
  imageChecks.set(key, Date.now());
  // Rewriting identical bytes would cost a full-size cache write per visit.
  if (existing && hash && hash === previousHash) return;

  await cache.put(
    key,
    buildEntry(buffer, response.headers.get("content-type"), hash)
  );
  originalHashes.set(key, hash);

  // Different bytes invalidate the derivative; identical bytes only restore an
  // original the sweep dropped, so the expensive canvas pass is not repeated.
  if (previousHash && hash && hash !== previousHash) {
    changedSources.add(key);
    await dropDerivative(key);
    logImageCacheEvent("INFO", "derivative_invalidated", { key, reason: "source_changed" });
  }
};

/** Whether this image was already checked against its source within the TTL. */
const checkedRecently = (key) =>
  Date.now() - (imageChecks.get(key) ?? 0) < SOFT_TTL_MS;

/** Checks run one at a time: a screenful of art must not burst the network. */
let checkChain = Promise.resolve();

/**
 * Queues a check for an image that has not had one recently, skipping
 * connections too slow to spend on speculative work.
 * @param {string} key
 * @param {() => Promise<void>} check
 */
const queueCheck = (key, check) => {
  if (fallbackMode || !prefetchAllowed()) return;
  if (!key || queuedChecks.has(key) || checkedRecently(key)) return;

  queuedChecks.add(key);
  whenIdle(() => {
    checkChain = checkChain
      .then(check)
      .catch((error) =>
        logImageCacheEvent("WARNING", "revalidate_failed", {
          error: getErrorMessage(error),
        })
      )
      .finally(() => queuedChecks.delete(key));
  });
};

/**
 * Queues a background refresh of a stored original.
 * @param {string} url
 */
export const revalidate = (url) => {
  queueCheck(canonicalImageKey(url), () => revalidateEntry(url));
};

/**
 * Whether an image's bytes were found to have changed while this page was open,
 * so anything derived from its in-memory copies should not be persisted.
 * @param {string} url
 * @returns {boolean}
 */
export const sourceChangedThisLoad = (url) =>
  changedSources.has(canonicalImageKey(url));

/**
 * Queues a check that a stored derivative still matches its source. A changed
 * or unverifiable source drops the derivative.
 * @param {string} url
 */
export const revalidateProcessed = (url) => {
  const key = canonicalImageKey(url);
  queueCheck(key, () => verifyDerivative(url, key));
};

/**
 * Deletes caches left behind by an older `IMAGE_CACHE_VERSION` or
 * `PROCESSING_VERSION`.
 * @returns {Promise<void>}
 */
export const flushStaleVersions = async () => {
  const storage = cacheStorage();
  if (fallbackMode || !storage) return;

  const names = await storage.keys();
  for (const name of names) {
    if (!name.startsWith(CACHE_PREFIX)) continue;
    if (name === ORIGINALS_CACHE || name === PROCESSED_CACHE) continue;

    await storage.delete(name);
    // A discarded derivative cache leaves the index pointing at nothing.
    if (name.includes("processed")) processedKeys.clear();
    logImageCacheEvent("INFO", "stale_version_flushed", { name });
  }
};

/**
 * Byte budget for one cache, reduced when the origin quota is tight.
 * @returns {Promise<number>}
 */
const resolveBudget = async () => {
  try {
    const { quota } = (await navigator.storage?.estimate?.()) ?? {};
    if (!quota) return CACHE_BYTE_BUDGET;
    return Math.min(CACHE_BYTE_BUDGET, (quota * QUOTA_SHARE) / 2);
  } catch {
    return CACHE_BYTE_BUDGET;
  }
};

/**
 * Drops entries past the hard TTL, then evicts least-recently-cached entries
 * until the cache fits its budget.
 * @param {string} name
 * @param {number} budget
 * @returns {Promise<void>}
 */
const sweepCache = async (name, budget) => {
  const cache = await openCache(name);
  if (!cache) return;

  // Dropping a derivative must also drop its index entry, or the synchronous
  // lookup keeps promising art the worker can no longer serve.
  const forget = async (request) => {
    await cache.delete(request);
    if (name !== PROCESSED_CACHE) return;
    const { pathname } = new URL(request.url);
    processedKeys.delete(
      decodeURIComponent(pathname.slice(PROCESSED_PATH_PREFIX.length))
    );
  };

  const requests = await cache.keys();
  const entries = [];
  const now = Date.now();

  for (const request of requests) {
    const response = await cache.match(request);
    if (!response) continue;

    const { cachedAt, bytes } = readMetadata(response);
    if (now - cachedAt > HARD_TTL_MS) {
      await forget(request);
      continue;
    }
    entries.push({ request, cachedAt, bytes });
  }

  let total = entries.reduce((sum, entry) => sum + entry.bytes, 0);
  if (total <= budget) return;

  entries.sort((a, b) => a.cachedAt - b.cachedAt);
  for (const entry of entries) {
    if (total <= budget) break;
    await forget(entry.request);
    total -= entry.bytes;
  }

  logImageCacheEvent("INFO", "sweep_completed", { name, bytes: total });
};

/**
 * Enforces TTL and byte budget across both caches.
 * @returns {Promise<void>}
 */
export const sweep = async () => {
  if (fallbackMode) return;

  const budget = await resolveBudget();
  await sweepCache(ORIGINALS_CACHE, budget);
  await sweepCache(PROCESSED_CACHE, budget);
};

/**
 * Whether enough time has passed since the last sweep, so a reload loop does not
 * walk every cache entry on each boot.
 * @returns {boolean}
 */
const sweepDue = () => {
  try {
    const last = Number(window.localStorage.getItem(SWEEP_STAMP_KEY)) || 0;
    if (Date.now() - last < SWEEP_INTERVAL_MS) return false;
    window.localStorage.setItem(SWEEP_STAMP_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

/**
 * Boot hook: discards caches from older versions and sweeps at most twice a day.
 */
export const initImageCache = () => {
  if (fallbackMode || !cacheStorage()) return;

  const failed = (error) =>
    logImageCacheEvent("ERROR", "init_failed", {
      error: getErrorMessage(error),
    });

  // Not deferred: the worker matches across every cache, so a superseded one has
  // to go before it can answer with a derivative this version rejects.
  flushStaleVersions()
    .then(() => whenIdle(() => (sweepDue() ? sweep().catch(failed) : undefined)))
    .catch(failed);
};
